import { FoodsIcon } from "@foodpilot/foods";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import { usePackagingDelete } from "src/api/client-api/packagings/useDeletePackaging";
import { packagingGridKey, PackagingList } from "src/api/client-api/packagings/useGetAllPackagings";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { CanUserCallback } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import type { ComponentAppearance } from "src/components/appearance";

type MenuItemsOptions = {
    appearance?: ComponentAppearance;
    companyIds: {
        "@id": IRI;
        id: number;
    };
    canUser: CanUserCallback;
};

export const usePackagingsMenuItems = (options: MenuItemsOptions) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const deletePackaging = usePackagingDelete();

    const { appearance = "legacy", companyIds } = options;

    const menuItemsAcc: MenuItemType<PackagingList>[] = [];

    menuItemsAcc.push({
        icon: <FoodsIcon icon="scoresIndicators" size={3} />,
        label: t("scores_indicateurs_cles"),
        onClick: (entity) => {
            navigate(URLHandler.packaging.scores(companyIds.id, entity.id));
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });

    menuItemsAcc.push({
        icon: <FoodsIcon icon="questionnaires" size={3} />,
        label: t("questionnaire"),
        onClick: (entity) => {
            navigate(URLHandler.packaging.ssq(companyIds.id, entity.id));
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });

    menuItemsAcc.push({
        icon: <FoodsIcon icon="informations" size={3} />,
        label: t("informations"),
        onClick: (entity) => {
            navigate(URLHandler.packaging.information(companyIds.id, entity.id));
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });

    menuItemsAcc.push({
        icon: appearance === "product-v2" ? <FoodsIcon icon="edit" size={3} /> : <Edit />,
        label: t("modifier"),
        onClick: (entity) => {
            navigate(URLHandler.packaging.edit(companyIds.id, entity.id, 0));
        },
        shouldShow: (entity) => {
            return entity?.canModify ?? false;
        },
    });

    menuItemsAcc.push({
        icon: appearance === "product-v2" ? <FoodsIcon icon="delete" size={3} /> : <Delete />,
        label: t("supprimer"),
        onClick: (entity) => {
            deletePackaging.mutate(entity.id, {
                onSuccess: () => {
                    queryClient.invalidateQueries([packagingGridKey]);
                },
            });
        },
        shouldShow: (entity) => {
            return entity?.canDelete ?? false;
        },
    });

    return menuItemsAcc;
};
